import {Banner, IconButton} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useState} from 'react';

import {REGEX_METRIC_MAX_LEN} from '../PanelRunsLinePlot/utils';

export const REGEX_METRIC_PREVIEW_INITIAL_LEN = 5;
export const REGEX_METRIC_PREVIEW_SHOW_MORE_LEN = 20;

export type RegexMetricPreviewData = {
  count: number;
  metrics: string[];
};

export function keepRegexActionsFromBlurringInput(
  event: React.MouseEvent<HTMLDivElement>
) {
  // Blur flushes the pending regex update. Keep action clicks from flushing a
  // stale regex before the clear/toggle handler runs.
  event.preventDefault();
}

export const RegexMetricPreview = ({
  preview,
  isExpanded,
  onToggleExpanded,
  // When the count is a paginated lower bound (more matches exist server-side
  // than we loaded), render it as "N+" and show the truncation note. The
  // history-key path knows the exact count, so it leaves this false.
  hasMore = false,
}: {
  preview: RegexMetricPreviewData;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  hasMore?: boolean;
}) => {
  // How many matches are listed when expanded. Owned here since only this
  // widget renders the list + "show more/less"; reset to the initial count
  // whenever the match set changes (derived-state pattern, no effect).
  const [numVisibleMetrics, setNumVisibleMetrics] = useState(
    REGEX_METRIC_PREVIEW_INITIAL_LEN
  );
  const [prevPreview, setPrevPreview] = useState(preview);
  if (preview !== prevPreview) {
    setPrevPreview(preview);
    setNumVisibleMetrics(REGEX_METRIC_PREVIEW_INITIAL_LEN);
  }

  const onShowMoreMetrics = () => {
    setNumVisibleMetrics(
      current => current + REGEX_METRIC_PREVIEW_SHOW_MORE_LEN
    );
  };
  const onShowLessMetrics = () => {
    setNumVisibleMetrics(REGEX_METRIC_PREVIEW_INITIAL_LEN);
  };

  const isMetricLimitExceeded = hasMore || preview.count > REGEX_METRIC_MAX_LEN;
  const displayedMetrics = preview.metrics.slice(0, numVisibleMetrics);
  const numMoreMetrics = preview.metrics.length - displayedMetrics.length;
  const numMetricsToShowMore = Math.min(
    REGEX_METRIC_PREVIEW_SHOW_MORE_LEN,
    numMoreMetrics
  );
  const hasExpandedContent = isExpanded || isMetricLimitExceeded;
  const hasMoreThanInitialMetrics =
    preview.metrics.length > REGEX_METRIC_PREVIEW_INITIAL_LEN;

  return (
    <TailwindContents>
      <div
        className="rounded-md bg-[light-dark(var(--moon-100),_var(--moon-850))] px-14 py-10 text-base text-[light-dark(var(--moon-800),_var(--moon-200))]"
        onMouseDown={event => event.preventDefault()}
        data-test="regex-metric-preview">
        <div
          className={`flex items-center justify-between gap-8 ${
            hasExpandedContent ? 'mb-8' : ''
          }`}>
          <div className="font-semibold text-[light-dark(var(--moon-700),_var(--moon-300))]">
            {preview.count.toLocaleString()}
            {hasMore ? '+' : ''} metrics matched
          </div>
          {preview.metrics.length > 0 && (
            <IconButton
              aria-label={
                isExpanded ? 'Hide metric matches' : 'Show metric matches'
              }
              data-dd-action-name={
                isExpanded ? 'Hide metric matches' : 'Show metric matches'
              }
              icon={isExpanded ? 'chevron-up' : 'chevron-down'}
              onPress={onToggleExpanded}
              size="sm"
              variant="ghost"
            />
          )}
        </div>
        {isMetricLimitExceeded && (
          <Banner
            className={isExpanded ? 'mb-8' : ''}
            isDismissible={false}
            size="sm"
            variant="warning">
            Only first {REGEX_METRIC_MAX_LEN} can be shown.
          </Banner>
        )}
        {isExpanded && preview.metrics.length > 0 && (
          <>
            <ul className="m-0 list-disc space-y-6 pl-24">
              {displayedMetrics.map(metric => (
                <li className="break-all leading-5" key={metric} title={metric}>
                  {metric}
                </li>
              ))}
            </ul>
            {numMoreMetrics > 0 ? (
              <button
                type="button"
                className="mt-8 text-left font-semibold text-wb-text-link hover:text-wb-text-link-hover"
                onClick={onShowMoreMetrics}>
                Show {numMetricsToShowMore} more
              </button>
            ) : (
              hasMoreThanInitialMetrics && (
                <button
                  type="button"
                  className="mt-8 text-left font-semibold text-wb-text-link hover:text-wb-text-link-hover"
                  onClick={onShowLessMetrics}>
                  Show less
                </button>
              )
            )}
          </>
        )}
      </div>
    </TailwindContents>
  );
};
